import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { workspaceApi, type Analytics } from '../../api/workspaces';

export function WorkspaceAnalyticsPage() {
  const { id = '' } = useParams();
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void workspaceApi
      .getAnalytics(id)
      .then(setAnalytics)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <p className="p-6 text-slate-600">Loading analytics…</p>;
  if (error || !analytics) return <p className="p-6 text-red-600">{error || 'Analytics unavailable'}</p>;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to={`/workspaces/${id}/dashboard`} className="text-sm text-slate-600 hover:underline">
        ← Dashboard
      </Link>
      <h1 className="mt-4 text-2xl font-semibold text-slate-900">Workspace Analytics</h1>
      <p className="mt-1 text-sm text-slate-600">Period: {analytics.period ?? '30d'}</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(analytics.metrics ?? {}).map(([key, value]) => (
          <div key={key} className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-sm text-slate-500">{key.replace(/_/g, ' ')}</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
          </div>
        ))}
      </div>

      {analytics.kpis && Object.keys(analytics.kpis).length > 0 && (
        <section className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-medium text-slate-900">KPIs</h2>
          <dl className="mt-4 grid gap-2 text-sm md:grid-cols-2">
            {Object.entries(analytics.kpis).map(([key, value]) => (
              <div key={key} className="flex justify-between gap-4 border-b border-slate-100 py-2">
                <dt className="text-slate-500">{key}</dt>
                <dd className="font-medium text-slate-900">{String(value)}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </div>
  );
}
